import React, { useReducer, useContext, type ReactNode } from 'react';
import axios from 'axios';

import { AuthState } from 'services/auth';
import { singular } from 'utils';
import { API_URL } from 'consts';
import type {
  NormaDefinition,
  OtpremnicaDefinition,
  RezijaDefinition,
  SifrarnikPage,
} from 'types/api';

const actionKey: Record<SifrarnikPage, string> = {
  norme: 'NORME',
  otpremnice: 'OTPREMNICE',
  rezije: 'REZIJE',
};

type SifrarnikItem = NormaDefinition | OtpremnicaDefinition | RezijaDefinition;

interface SifrarnikStateShape {
  loading: boolean;
  error: unknown;
  norme: NormaDefinition[] | null;
  otpremnice: OtpremnicaDefinition[] | null;
  rezije: RezijaDefinition[] | null;
}

interface SifrarnikContextValue extends SifrarnikStateShape {
  get: (page: SifrarnikPage) => Promise<void>;
  post: (page: SifrarnikPage, data: Record<string, unknown>) => Promise<void>;
  deleteRes: (page: SifrarnikPage, id: string | number) => Promise<void>;
}

interface SifrarnikAction {
  type: string;
  payload?: NormaDefinition[] | OtpremnicaDefinition[] | RezijaDefinition[];
  error?: unknown;
}

const initialState: SifrarnikStateShape = {
  loading: false,
  error: null,
  norme: null,
  otpremnice: null,
  rezije: null,
};

const noopAsync = async () => {};

export const SifrarnikState = React.createContext<SifrarnikContextValue>({
  ...initialState,
  get: noopAsync,
  post: noopAsync,
  deleteRes: noopAsync,
});

const reducer = (state: SifrarnikStateShape, action: SifrarnikAction): SifrarnikStateShape => {
  switch (action.type) {
    case 'FETCH_NORME_START':
    case 'FETCH_OTPREMNICE_START':
    case 'FETCH_REZIJE_START':
    case 'POST_NORME_START':
    case 'POST_OTPREMNICE_START':
    case 'POST_REZIJE_START':
    case 'DELETE_NORME_START':
    case 'DELETE_OTPREMNICE_START':
    case 'DELETE_REZIJE_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_NORME_SUCCESS':
      return { ...state, loading: false, norme: action.payload as NormaDefinition[] };
    case 'FETCH_OTPREMNICE_SUCCESS':
      return { ...state, loading: false, otpremnice: action.payload as OtpremnicaDefinition[] };
    case 'FETCH_REZIJE_SUCCESS':
      return { ...state, loading: false, rezije: action.payload as RezijaDefinition[] };
    case 'POST_NORME_SUCCESS':
    case 'POST_OTPREMNICE_SUCCESS':
    case 'POST_REZIJE_SUCCESS':
    case 'DELETE_NORME_SUCCESS':
    case 'DELETE_OTPREMNICE_SUCCESS':
    case 'DELETE_REZIJE_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_NORME_FAIL':
    case 'FETCH_OTPREMNICE_FAIL':
    case 'FETCH_REZIJE_FAIL':
    case 'POST_NORME_FAIL':
    case 'POST_OTPREMNICE_FAIL':
    case 'POST_REZIJE_FAIL':
    case 'DELETE_NORME_FAIL':
    case 'DELETE_OTPREMNICE_FAIL':
    case 'DELETE_REZIJE_FAIL':
      return { ...state, loading: false, error: action.error };
    default:
      return state;
  }
};

export const SifrarnikProvider = ({ children }: { children: ReactNode }) => {
  const { access_token } = useContext(AuthState);
  const [state, dispatch] = useReducer(reducer, initialState);

  const get = async (page: SifrarnikPage) => {
    dispatch({ type: `FETCH_${actionKey[page]}_START` });

    try {
      const res = await axios.get<SifrarnikItem[]>(`${API_URL}/${singular(page)}`, {
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({
        type: `FETCH_${actionKey[page]}_SUCCESS`,
        payload: res.data,
      });
    } catch (error) {
      dispatch({ type: `FETCH_${actionKey[page]}_FAIL`, error });
    }
  };

  const post = async (page: SifrarnikPage, data: Record<string, unknown>) => {
    dispatch({ type: `POST_${actionKey[page]}_START` });

    try {
      await axios({
        method: 'post',
        data,
        url: `${API_URL}/${singular(page)}`,
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: `POST_${actionKey[page]}_SUCCESS` });
    } catch (error) {
      dispatch({ type: `POST_${actionKey[page]}_FAIL`, error });
    }
  };

  const deleteRes = async (page: SifrarnikPage, id: string | number) => {
    dispatch({ type: `DELETE_${actionKey[page]}_START` });

    try {
      await axios({
        method: 'delete',
        url: `${API_URL}/${singular(page)}/${id}`,
        headers: {
          authorization: `Bearer: ${access_token}`,
        },
      });

      dispatch({ type: `DELETE_${actionKey[page]}_SUCCESS` });
    } catch (error) {
      dispatch({ type: `DELETE_${actionKey[page]}_FAIL`, error });
    }
  };

  const value: SifrarnikContextValue = {
    ...state,
    get,
    post,
    deleteRes,
  };

  return <SifrarnikState.Provider value={value}>{children}</SifrarnikState.Provider>;
};
